import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, ViewChild } from '@angular/core';
import { CommonDataService } from '@sb-tools/typescript/services/common-data.service';
import { Subject } from 'rxjs/internal/Subject';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { UserDataService } from '@sb-tools/typescript/services/user-data.service';
import { SearchTicketsService } from '@sb-tools/typescript/services/search-tickets.service';
import { SimpleMessageDTO } from '@sb-tools/typescript/models/dto/simple-message.dto';
import { ActivatedRoute, RouterLink, RouterLinkActive } from '@angular/router';
import { FieldType } from '@sb-tools/typescript/enums/field-type.enum';
import { DatePipe, NgClass } from '@angular/common';
import { Field } from 'src/sb-tools/typescript/models/field.model';
import { FormsModule, NgForm } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { HelperService } from '@sb-tools/typescript/services/helper.service';
import { BaseUserWithRole } from '@sb-tools/typescript/models/base-user-with-role.model';
import { RightPanelType } from '../../../core/enums/right-panel-type.enum';
import { OpenTicketInNewWindowService } from '../../../core/services/ticket/open-ticket-in-new-window.service';
import { RightPanelService } from '../../../core/services/right-panel.service';
import { FieldsService } from '../../../core/services/ticket/fields.service';
import { TicketService } from '../../../core/services/ticket/ticket.service';
import { TicketRightPanelService } from '../../../core/services/ticket-right-panel.service';
import { TicketDetailsPanelService } from '../../../core/services/ticket-details-panel.service';
import { UserAvatarComponent } from '../../../../sb-modules/Frontend-User-Avatar-Module/user-avatar.component';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { InlineSVGComponent } from '../../../../sb-modules/Frontend-Inline-SVG-Module/inline-svg.component';
import { TranslateModule } from '@ngx-translate/core';
import { FocusDirective } from '../../../../sb-tools/typescript/directives/focus.directive';
import { ClipboardModule } from 'ngx-clipboard';
import { TicketSourcePipe } from '../../../../sb-tools/typescript/pipes/ticket-source.pipe';
import { RoleNamePipe } from '../../../../sb-tools/typescript/pipes/role-name.pipe';
import { TimeAgoPipe } from '../../pipes/time-ago.pipe';

@Component({
    selector: 'app-user-info',
    templateUrl: './user-info.component.html',
    styleUrls: ['./user-info.component.scss'],
    imports: [
        UserAvatarComponent,
        TooltipModule,
        InlineSVGComponent,
        FormsModule,
        NgClass,
        TranslateModule,
        FocusDirective,
        ClipboardModule,
        RouterLinkActive,
        RouterLink,
        DatePipe,
        TicketSourcePipe,
        RoleNamePipe,
        TimeAgoPipe,
    ],
})
export class UserInfoComponent implements OnChanges, OnDestroy {
    @ViewChild('userForm') userForm: NgForm;

    @Input() isShowEditUserForm?: boolean;
    @Input() user?: BaseUserWithRole;
    @Input() userId?: string;

    rightPanelTypeEnum = RightPanelType;
    emailValidationPattern = HelperService.emailValidationPattern;
    fields: Field[] = [];
    isCanBeEdited: boolean;
    isEditFormOpened: boolean;
    isFirstMessageCreator: boolean;
    isSubmitted: boolean;
    isUpdating: boolean;
    isUserLoading = true;
    userLastTickets: SimpleMessageDTO[];
    userModel: {
        userId: string;
        name: string;
        email: string;
    };

    private unsubscriber = new Subject<void>();

    constructor(
        public openTicketInNewWindowService: OpenTicketInNewWindowService,
        public rightPanelService: RightPanelService,
        public commonDataService: CommonDataService,
        private userDataService: UserDataService,
        private changeDetectorRef: ChangeDetectorRef,
        public searchTicketsService: SearchTicketsService,
        public fieldsService: FieldsService,
        private activatedRoute: ActivatedRoute,
        private ticketService: TicketService,
        private ticketRightPanelService: TicketRightPanelService,
        public ticketDetailsPanelService: TicketDetailsPanelService,
    ) {}

    ngOnChanges() {
        if (this.user) {
            this.userId = this.user.id;
            this.processUser();
        } else {
            this.userDataService
                .get(this.userId)
                .pipe(takeUntil(this.unsubscriber))
                .subscribe((data) => {
                    this.user = data;
                    this.processUser();
                });
        }

        if (this.isShowEditUserForm) {
            this.openUserEditForm();

            this.ticketRightPanelService.openUserEditFormSubject.pipe(takeUntil(this.unsubscriber)).subscribe(() => {
                if (!this.isEditFormOpened) {
                    this.openUserEditForm();
                }
            });
        }

        this.searchTicketsService
            .getUserPreviousMessages(this.userId)
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((tickets) => {
                this.userLastTickets = tickets;
            });

        this.fieldsService.fieldsUpdated.pipe(takeUntil(this.unsubscriber)).subscribe(() => {
            this.fields = this.fieldsService.currentFields?.filter((field) => {
                return field.options.isShowInUserInfo === 'true';
            });
            this.fields = this.transformFields(this.fields);
        });
    }

    openUserEditForm(): void {
        this.userModel = {
            userId: this.user.id,
            email: this.user.email,
            name: this.user.name,
        };
        this.isEditFormOpened = true;
    }

    transformFields(fields: Field[]) {
        fields.forEach((field: Field) => {
            if (field.type === FieldType.date) {
                field.transformedValue = this.transformToLocalDate(field.value);
            } else {
                field.transformedValue = field.value;
            }
        });
        return fields;
    }

    transformToLocalDate(date) {
        if (!date) {
            return '';
        }
        const isDateWithTime = date.length > 10;
        return isDateWithTime
            ? new DatePipe('en').transform(date, 'dd.MM.yyyy HH:mm')
            : new DatePipe('en').transform(date, 'dd.MM.yyyy HH:mm').substring(0, 10);
    }

    updateUserInfo(): void {
        this.isSubmitted = true;

        if (this.userForm.valid) {
            this.isUpdating = true;

            this.ticketService
                .updateFirstMessageCreator(this.ticketDetailsPanelService.currentTicket.id, this.userModel)
                .pipe(
                    finalize(() => {
                        this.isUpdating = false;
                    }),
                    takeUntil(this.unsubscriber),
                )
                .subscribe((user) => {
                    this.isSubmitted = false;
                    this.isEditFormOpened = false;
                    this.isShowEditUserForm = false;
                    this.ticketDetailsPanelService.currentTicket.firstMessageCreator = user;
                    this.user = user;
                });
        }
    }

    private getFields(ticketId): void {
        this.fieldsService
            .getAll(ticketId)
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data: Field[]) => {
                this.fieldsService.currentFields = data;
                this.fieldsService.isGotFields = true;
                this.fields = data?.filter((field) => {
                    return field.options.isShowInUserInfo === 'true';
                });
                this.fields = this.transformFields(this.fields);
            });
    }

    private loadFields(): void {
        if (!this.fieldsService.isGotFields && this.fieldsService.ticketCreatorId === this.userId) {
            this.getFields(this.activatedRoute.snapshot.queryParams['ticketId']);
        } else {
            this.fields = this.fieldsService.currentFields?.filter((field) => {
                return field.options.isShowInUserInfo === 'true';
            });
            this.fields = this.transformFields(this.fields);
        }
    }

    private processUser() {
        this.isFirstMessageCreator =
            this.user.id === this.ticketDetailsPanelService.currentTicket?.firstMessageCreator.id;

        this.isCanBeEdited =
            !this.commonDataService.isOneTime &&
            this.isFirstMessageCreator &&
            this.ticketDetailsPanelService.currentTicket.constraints.isFirstMessageCreatorEditable;

        this.loadFields();
        this.isUserLoading = false;

        this.changeDetectorRef.detectChanges();
    }

    ngOnDestroy() {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
